import React, { useEffect, useState } from "react";
import { ExternalLink, KeyRound, RefreshCw, Save } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  getPlatformIntegrations,
  updatePlatformIntegration,
} from "../../services/api";

type ProviderKey =
  | "baseEnv"
  | "mail"
  | "applePasskit"
  | "digitalOceanSpaces"
  | "square";

type ProviderStatus = {
  provider: ProviderKey;
  label: string;
  configured: boolean;
  accountEmail: string;
  updatedAt: string | null;
  env: Record<string, string>;
};

const PROVIDERS: Array<{
  key: ProviderKey;
  title: string;
  description: string;
  docsUrl: string;
  signupUrl: string;
  accountHelp: string;
  fields: Array<{ key: string; label: string; required: boolean }>;
}> = [
  {
    key: "mail",
    title: "Mail",
    description: `Configure mail provider key (Resend) and sender settings used for OTP and promotions.Add ${process.env.REACT_APP_API_URL} as BASE_URL`,
    docsUrl: "https://resend.com/docs",
    signupUrl: "https://resend.com/",
    accountHelp: "Use the email for your mail service account.",
    fields: [
      { key: "MAIL_KEY", label: "MAIL_KEY", required: true },
      { key: "MAIL_FROM", label: "MAIL_FROM", required: true },
      { key: "BASE_URL", label: "BASE_URL", required: false },
    ],
  },
  {
    key: "applePasskit",
    title: "Apple PassKit",
    description: `Configure Apple PassKit certificate password and base URL. Add ${process.env.REACT_APP_API_URL} as BASE_URL.`,
    docsUrl: "https://developer.apple.com/wallet/",
    signupUrl: "https://developer.apple.com/account/",
    accountHelp: "Use the Apple developer account email.",
    fields: [
      {
        key: "PASS_CERTIFICATE_PASSWORD",
        label: "PASS_CERTIFICATE_PASSWORD",
        required: true,
      },
      { key: "BASE_URL", label: "BASE_URL", required: false },
    ],
  },
  // {
  //   key: "digitalOceanSpaces",
  //   title: "DigitalOcean Spaces",
  //   description:
  //     "Configure object storage credentials and bucket details for uploads.",
  //   docsUrl: "https://docs.digitalocean.com/products/spaces/",
  //   signupUrl: "https://cloud.digitalocean.com/",
  //   accountHelp: "Use your DigitalOcean account email.",
  //   fields: [
  //     { key: "DO_SPACES_KEY", label: "DO_SPACES_KEY", required: true },
  //     { key: "DO_SPACES_SECRET", label: "DO_SPACES_SECRET", required: true },
  //     {
  //       key: "DO_SPACES_ENDPOINT",
  //       label: "DO_SPACES_ENDPOINT",
  //       required: true,
  //     },
  //     { key: "DO_SPACES_BUCKET", label: "DO_SPACES_BUCKET", required: true },
  //     { key: "DO_SPACES_REGION", label: "DO_SPACES_REGION", required: true },
  //   ],
  // },
  {
    key: "square",
    title: "Square (Platform)",
    description:
      "Platform-level Square credentials used as a fallback when a location has no Square keys configured. Per-location keys always take priority.",
    docsUrl: "https://developer.squareup.com/docs",
    signupUrl: "https://squareup.com/us/en/developers",
    accountHelp: "Use your Square Developer account email.",
    fields: [
      {
        key: "SQUARE_ACCESS_TOKEN",
        label: "SQUARE_ACCESS_TOKEN",
        required: true,
      },
      {
        key: "SQUARE_LOCATION_ID",
        label: "SQUARE_LOCATION_ID",
        required: true,
      },
      {
        key: "SQUARE_APPLICATION_ID",
        label: "SQUARE_APPLICATION_ID",
        required: true,
      },
      {
        key: "SQUARE_ENVIRONMENT",
        label: "SQUARE_ENVIRONMENT",
        required: true,
      },
      {
        key: "SQUARE_WEBHOOK_SIGNATURE_KEY",
        label: "SQUARE_WEBHOOK_SIGNATURE_KEY",
        required: false,
      },
      {
        key: "SQUARE_BASE_URL",
        label: "SQUARE_BASE_URL (Webhook URL)",
        required: true,
      },
    ],
  },
];

const createDefaultForms = () =>
  PROVIDERS.reduce(
    (acc, provider) => {
      acc[provider.key] = { accountEmail: "", credentials: {} };
      return acc;
    },
    {} as Record<
      ProviderKey,
      { accountEmail: string; credentials: Record<string, string> }
    >,
  );

const DeveloperIntegrations: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<ProviderKey | null>(null);
  const [statusMap, setStatusMap] = useState<
    Record<ProviderKey, ProviderStatus>
  >({} as Record<ProviderKey, ProviderStatus>);
  const [forms, setForms] =
    useState<
      Record<
        ProviderKey,
        { accountEmail: string; credentials: Record<string, string> }
      >
    >(createDefaultForms());

  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      const res = await getPlatformIntegrations();
      const list = (res.data?.data || []) as ProviderStatus[];
      const nextStatusMap = {} as Record<ProviderKey, ProviderStatus>;
      const nextForms = createDefaultForms();

      list.forEach((item) => {
        nextStatusMap[item.provider] = item;
        if (nextForms[item.provider]) {
          nextForms[item.provider].accountEmail = item.accountEmail || "";
        }
      });

      setStatusMap(nextStatusMap);
      setForms(nextForms);
    } catch (error) {
      toast.error("Failed to load integration settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const onFieldChange = (provider: ProviderKey, key: string, value: string) => {
    setForms((prev) => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        credentials: {
          ...prev[provider].credentials,
          [key]: value,
        },
      },
    }));
  };

  const onEmailChange = (provider: ProviderKey, value: string) => {
    setForms((prev) => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        accountEmail: value,
      },
    }));
  };

  const onSave = async (provider: ProviderKey) => {
    setSaving(provider);
    try {
      await updatePlatformIntegration(provider as any, forms[provider]);
      toast.success("Credentials saved securely");
      setForms((prev) => ({
        ...prev,
        [provider]: {
          ...prev[provider],
          credentials: {},
        },
      }));
      await fetchIntegrations();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to save credentials");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <KeyRound size={28} className="text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-800 m-0">
            Integration & Environment Keys
          </h1>
        </div>
        <button
          onClick={fetchIntegrations}
          className="h-10 w-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
          title="Refresh"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      <p className="text-gray-600 mb-6">
        Save credentials dynamically from Admin. Values are encrypted in the
        backend database and used as runtime fallbacks when available.
      </p>

      <div className="space-y-6">
        {PROVIDERS.map((provider) => {
          const status = statusMap[provider.key];
          const form = forms[provider.key];

          return (
            <div
              key={provider.key}
              className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            >
              <div className="flex flex-col items-start md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {provider.title}
                  </h2>
                  <p className="text-gray-600">{provider.description}</p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    status?.configured
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {status?.configured ? "Configured" : "Not Configured"}
                </span>
              </div>

              <div className="flex flex-wrap gap-3 mb-5">
                <a
                  href={provider.signupUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  Open Provider <ExternalLink size={16} />
                </a>
                <a
                  href={provider.docsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Docs <ExternalLink size={16} />
                </a>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Email
                </label>
                <input
                  type="email"
                  value={form.accountEmail}
                  onChange={(e) => onEmailChange(provider.key, e.target.value)}
                  placeholder={provider.accountHelp}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {provider.fields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                      {field.required ? " *" : ""}
                    </label>
                    <input
                      type="password"
                      value={form.credentials[field.key] || ""}
                      onChange={(e) =>
                        onFieldChange(provider.key, field.key, e.target.value)
                      }
                      placeholder={
                        status?.env?.[field.key]
                          ? `Existing: ${status.env[field.key]}`
                          : "Paste value"
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
              </div>

              {status?.updatedAt && (
                <p className="text-xs text-gray-500 mb-4">
                  Last updated: {new Date(status.updatedAt).toLocaleString()}
                </p>
              )}

              <button
                onClick={() => onSave(provider.key)}
                disabled={saving === provider.key}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
              >
                <Save size={16} />
                {saving === provider.key ? "Saving..." : "Save Credentials"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DeveloperIntegrations;
